/**
 * @fileoverview The Gibbs action dispatcher -- the registered seam between main and the Gibbs
 * tree (proto/src/gibbs/). See docs/gibbs-migration-register.md, Slice D item 1.
 *
 * interpretActionPlan takes an ActionPlan from the text-to-action pipeline and executes a
 * single_action plan through the registry: look the ActionDef up, gate on status, validate the
 * plan, bind the declared inputs and parameters, and dispatch -- to a UserAction, a BFD server
 * command, or a job submission. Workflow, chat and clarify plans pass through for the UI.
 *
 * This module is the only file outside gibbs/ allowed to import from it
 * (test/bmaps/gibbsImportBoundary.test.js). The two imports are dynamic to avoid a load-time
 * cycle: cmds -> gibbs -> cmds. CmdInterpreter keeps a static forwarder to interpretActionPlan
 * so callers, and the fork's tests that patch it, keep the CmdInterpreter.interpretActionPlan
 * name.
 *
 * The dispatcher arrived from the fork's CmdInterpreter.js (payload 5736c4bfc) and was moved
 * here unchanged apart from the argStyle branch and this seam; see the register for what was
 * corrected and what was recorded rather than fixed (the server-command route).
 */
import { UserActions } from 'BMapsCmds';
import { App } from 'BMapsSrc/BMapsApp';
import { energyErrorText, parseEnergyError } from './cmds_common';

/**
 * How the special values "active", "current" and "all" resolve to workspace objects, by the
 * DECLARED type of the input or parameter they were supplied for. A value supplied for anything
 * else -- a string option like export-to-format's `target: 'all'` or the hydrogen view's
 * `mode: 'all'` -- is a literal and passes through untouched. (The fork guessed from the
 * parameter's name instead, which turned `target: 'all'` into an array of case data.)
 */
const SPECIAL_VALUE_RESOLVERS = {
    // A single Compound cannot be "all"; that literal stays a literal (activate-compound).
    Compound: {
        active: (ws) => ws.getActiveCompound?.() ?? null,
    },
    CompoundList: {
        active: (ws) => ws.getActiveCompound?.() ?? null,
        all: (ws) => ws.getLoadedCompounds?.() || [],
    },
    CaseData: {
        current: (ws) => ws.firstCaseData?.() || null,
        all: (ws) => ws.allCaseData?.() || [],
    },
    'CaseData[]': {
        current: (ws) => ws.firstCaseData?.() || null,
        all: (ws) => ws.allCaseData?.() || [],
    },
    // No entry for ProteinStructure: the registry declares it only on outputs.
};

/**
 * The types the table above resolves, each with its special values. The registry's
 * PLAN_SPECIAL_VALUES must match; test/gibbs/dispatchTargets.test.js checks it. Exported for
 * that test.
 */
export const SPECIAL_VALUES = Object.freeze(Object.fromEntries(
    Object.entries(SPECIAL_VALUE_RESOLVERS).map(([type, byValue]) => [type, Object.keys(byValue)])
));

/**
 * Resolve one supplied value against the ParamDef it was supplied for. Exported for its unit
 * test; the dispatcher passes App.Workspace.
 *
 * @param {*} value - the value the plan supplied
 * @param {{ name: string, type: string } | undefined} def - the declared input or parameter
 * @param {object} workspace - App.Workspace or a stand-in
 */
export function resolveSpecialValue(value, def, workspace) {
    if (typeof value !== 'string' || !def) return value;
    const resolver = SPECIAL_VALUE_RESOLVERS[def.type]?.[value];
    return resolver ? resolver(workspace) : value;
}

/** The declared inputs and parameters of an ActionDef, by name. */
function declaredParams(actionDef) {
    const byName = new Map();
    for (const def of [...actionDef.inputs, ...actionDef.parameters]) byName.set(def.name, def);
    return byName;
}

/**
 * The declared inputs that were supplied, keyed by name: the shape an `argStyle: 'options'`
 * action hands to its userAction (see gibbs/registry/schema.js). Undefined values are left out
 * so an optional input that was not given does not appear as an explicit `undefined` key.
 */
function namedInputs(actionDef, resolvedParams) {
    const named = {};
    for (const input of actionDef.inputs) {
        const value = resolvedParams[input.name];
        if (value !== undefined) named[input.name] = value;
    }
    return named;
}

function returnedErrorMessage(error) {
    // The server's JSON-encoded errors, worded as the Selector words them.
    const parsed = parseEnergyError(error);
    if (parsed?.errType) return energyErrorText(parsed);
    if (typeof error === 'string') return error;
    if (error && typeof error.message === 'string') return error.message;
    if (error && typeof error.code === 'string') return error.code;
    return 'Action reported an error.';
}

/**
 * Record one dispatch result without letting a returned failure pass as success. Main's commands
 * report failure three ways: a throw (the dispatcher catches it), a singular `error`, or a
 * non-empty `errors` array, which most server-backed commands return. The last two are reported
 * here, and a result carrying `errors` is still kept.
 */
function recordActionResult(results, entry) {
    const { result } = entry;
    if (result?.error) {
        results.errors.push(returnedErrorMessage(result.error));
        return;
    }
    if (Array.isArray(result?.errors) && result.errors.length > 0) {
        results.errors.push(...result.errors.map(returnedErrorMessage));
    }
    results.results.push(entry);
}

/**
 * Dispatch a BFD server command or search service request.
 * Handles actions that talk directly to the server without
 * a UserAction wrapper (translate-molecule, search endpoints,
 * property lookups, etc.).
 *
 * @param {string} command - Server command name
 * @param {Object} params - Resolved parameters
 * @param {Object} actionDef - Full ActionDef for context
 * @returns {Promise<Object>} Server response
 */
export async function dispatchServerCommand(command, params, actionDef) {
    const { ServerCmds } = await import('../server_cmds');

    // Map serverCommand names to ServerCmd objects
    const cmdMap = {
        'translate-molecule': ServerCmds.TranslateMolecule,
        'get-molecule-properties': ServerCmds.GetMoleculeProperties,
        'get-unbound-conformations': ServerCmds.GetUnboundConformations,
        'export-selection': ServerCmds.ExportSelection,
        select: ServerCmds.Select,
        'frag-data-query': ServerCmds.FragDataQuery,
    };

    const serverCmd = cmdMap[command];
    if (serverCmd) {
        return serverCmd.execute(params);
    }

    // Search/edit service endpoints (HTTP, not WebSocket)
    const { FragservWs, WebServices } = await import('../WebServices');
    if (command === 'lookupByName') {
        return FragservWs.fragLookupByName(params.names);
    }
    const serviceMap = {
        substructureSearch: {
            url: `${WebServices.FRAGSERV_HOST}/edit/substructureSearch`,
            method: 'POST',
        },
        searchEngine: {
            url: `${WebServices.FRAGSERV_HOST}/search/searchEngine`,
            method: 'POST',
        },
        getMolPic: {
            url: `${WebServices.FRAGSERV_HOST}/edit/pubpic/`,
            method: 'GET',
        },
        getProperties: {
            url: `${WebServices.FRAGSERV_HOST}/search/properties/`,
            method: 'POST',
        },
    };

    const svc = serviceMap[command];
    if (svc) {
        const response = await WebServices.startWsRequest(
            svc.url,
            svc.method === 'POST' ? JSON.stringify(params) : null
        );
        return typeof response === 'string'
            ? JSON.parse(response)
            : response;
    }

    throw new Error(
        `Unknown serverCommand: ${command}`
        + ` for action ${actionDef.id}`
    );
}

/**
 * Submit an async background job via the JobManager.
 * Handles folding, docking, scoring, REINVENT, MapLight,
 * and library screening jobs.
 *
 * @param {string} jobType - Job type from JobTypes enum
 * @param {Object} params - Resolved parameters
 * @param {Object} actionDef - Full ActionDef for context
 * @returns {Promise<Object>} Job submission result with jobId
 */
export async function dispatchJobSubmission(jobType, params, actionDef) {
    const { postJob } = await import('../jobman/JobsApi');

    const jobrequest = { ...params };
    const stash = {
        actionId: actionDef.id,
        actionName: actionDef.name,
    };

    const { results, error } = await postJob({
        jobtype: jobType,
        jobrequest,
        stash,
    });

    if (error) {
        throw new Error(
            `Job submission failed for ${actionDef.id}: ${error}`
        );
    }

    return {
        jobId: results.jobid,
        jobType,
        state: results.state || 'starting',
        message: `${actionDef.name} job submitted`,
    };
}

/**
 * Dispatch an ActionDef-based action plan from the Gibbs
 * text-to-action pipeline.
 *
 * For single_action plans: resolves parameters and executes
 * via UserActions, server commands, or job submission.
 * For workflow plans: returns the plan as pending
 * (for Workflow Checker UI).
 * For clarification/chat: passes through for UI rendering.
 *
 * @param {Object} plan - ActionPlan from textToAction()
 * @returns {Promise<Object>} Execution result
 */
export async function interpretActionPlan(plan) {
    if (!plan || !plan.intent) {
        return { type: 'error', error: 'Invalid plan: missing intent' };
    }

    // Chat and clarification pass through for UI
    if (plan.intent === 'chat') {
        return { type: 'chat', message: plan.message || '' };
    }
    if (plan.intent === 'clarify') {
        return { type: 'clarification', clarification: plan };
    }

    // Workflow plans returned as pending — Workflow Checker UI handles confirmation
    if (plan.intent === 'workflow') {
        return {
            type: 'pending_workflow',
            plan,
            summary: plan.summary || 'Multi-step workflow ready for review',
        };
    }

    // Single action: execute immediately
    if (plan.intent === 'single_action' && plan.actions?.length > 0) {
        const planned = plan.actions[0];
        const results = { type: 'executed', results: [], errors: [] };

        try {
            // Dynamic import to avoid circular dependency at module load time.
            // This module is the registered seam of the Gibbs import boundary
            // (test/bmaps/gibbsImportBoundary.test.js); this import and the one below are it.
            // See docs/gibbs-migration-register.md, Slice D item 1.
            const {
                getAction, uncarriableParams, planValueProblem,
            } = await import('../gibbs/registry');
            const actionDef = getAction(planned.actionId);

            if (!actionDef) {
                results.errors.push(`Unknown action: ${planned.actionId}`);
                return results;
            }

            if (actionDef.status !== 'implemented') {
                results.errors.push(
                    `Action "${planned.actionId}" is not implemented`
                    + ` (status: ${actionDef.status || 'unspecified'}).`
                );
                return results;
            }

            // Built, but its call does not fit this tree yet (schema.js `blockedOn`). Refused
            // here rather than misbehaving in the callee; the blocked set is pinned by
            // test/gibbs/dispatchTargets.test.js.
            if (actionDef.blockedOn) {
                results.errors.push(
                    `Action "${planned.actionId}" is not implemented on this build`
                    + ` (blocked on: ${actionDef.blockedOn}).`
                );
                return results;
            }

            // A value a plan cannot carry would reach the callee as a name or as unchecked data
            // where it needs a live object (registry types.js). A required one means the action
            // cannot run from a plan; a supplied one must be a value the plan can carry -- for
            // a resolved type, only its special values.
            const uncarried = uncarriableParams(actionDef);
            for (const def of uncarried) {
                results.errors.push(
                    `Action "${planned.actionId}" cannot take "${def.name}" from a plan`
                    + ` (type ${def.type}, which a plan cannot carry).`
                );
            }
            const supplied = { ...(planned.params || {}), ...(planned.resolvedInputs || {}) };
            const defs = declaredParams(actionDef);
            for (const [key, value] of Object.entries(supplied)) {
                if (uncarried.some((def) => def.name === key)) continue;
                const problem = planValueProblem(defs.get(key), value);
                if (problem) results.errors.push(`Action "${planned.actionId}": ${problem}.`);
            }
            if (results.errors.length > 0) return results;

            // A destructive action runs only on a plan that carries an explicit confirmation.
            // The planner stages such a plan and sets `confirmed: true` only when the user
            // confirms it (gibbs/text-to-action/resumeFromClarification.js); nothing else may
            // set it, so nothing destructive dispatches unasked.
            if (actionDef.destructiveConfirm === true && plan.confirmed !== true) {
                results.errors.push(
                    `Action "${planned.actionId}" is destructive and needs confirmation:`
                    + ` ${actionDef.confirmMessage}`
                );
                return results;
            }

            const { validatePlan } = await import('../gibbs/text-to-action/validatePlan');
            const validation = validatePlan(plan);
            if (!validation.valid) {
                results.errors.push(...validation.errors);
                return results;
            }

            // Resolve special values ("active", "current", "all") by the declared type of the
            // input or parameter each was supplied for.
            const resolvedParams = {};
            for (const [key, value] of Object.entries(supplied)) {
                resolvedParams[key] = resolveSpecialValue(value, defs.get(key), App.Workspace);
            }

            // Required inputs are nullish-invalid. Preserve optional positional holes so a
            // missing earlier input can never re-index a later input at dispatch.
            const missingRequired = actionDef.inputs
                .filter((input) => input.required !== false
                    && resolvedParams[input.name] == null)
                .map((input) => input.name);
            if (missingRequired.length > 0) {
                results.errors.push(
                    `Missing required input(s) for ${planned.actionId}: ${missingRequired.join(', ')}`
                );
                return results;
            }
            const inputArgs = actionDef.inputs
                .map((input) => resolvedParams[input.name]);
            const paramObj = {};
            for (const p of actionDef.parameters) {
                if (resolvedParams[p.name] !== undefined) {
                    paramObj[p.name] = resolvedParams[p.name];
                }
            }

            // Dispatch priority: userAction → serverCommand → jobType
            if (actionDef.userAction
                && UserActions[actionDef.userAction]) {
                // Route 1: UserAction function (most actions). The declaration is the
                // signature: inputs spread positionally in declared order, parameters as one
                // trailing options object. An ActionDef with `argStyle: 'options'` instead
                // hands its callee ONE named object holding inputs and parameters alike --
                // the shape main's named-object commands take (alignProtein(options)).
                const args = actionDef.argStyle === 'options'
                    ? [{ ...namedInputs(actionDef, resolvedParams), ...paramObj }]
                    : [...inputArgs];
                if (actionDef.argStyle !== 'options' && Object.keys(paramObj).length > 0) {
                    args.push(paramObj);
                }
                const result = await UserActions[
                    actionDef.userAction
                ](...args);
                recordActionResult(results, {
                    actionId: planned.actionId,
                    actionName: actionDef.name,
                    result,
                    displayText: actionDef.name,
                });
            } else if (actionDef.serverCommand) {
                // Route 2: BFD server command (translate,
                // properties, search service endpoints)
                const result = await dispatchServerCommand(
                    actionDef.serverCommand,
                    resolvedParams,
                    actionDef
                );
                recordActionResult(results, {
                    actionId: planned.actionId,
                    actionName: actionDef.name,
                    result,
                    displayText: actionDef.name,
                });
            } else if (actionDef.jobType) {
                // Route 3: Async job submission (folding,
                // docking, screening, REINVENT, MapLight)
                const result = await dispatchJobSubmission(
                    actionDef.jobType,
                    resolvedParams,
                    actionDef
                );
                recordActionResult(results, {
                    actionId: planned.actionId,
                    actionName: actionDef.name,
                    result,
                    displayText: `${actionDef.name} (job submitted)`,
                });
            } else {
                results.errors.push(
                    `Action "${planned.actionId}" has no`
                    + ' executable dispatch route (userAction:'
                    + ` ${actionDef.userAction}, serverCommand:`
                    + ` ${actionDef.serverCommand}, jobType:`
                    + ` ${actionDef.jobType})`
                );
            }
            // (macro recording is fed from UserAction.do() — the universal
            // seam that both manual and Gibbs-dispatched actions flow
            // through — so no publish is needed here.)
        } catch (err) {
            results.errors.push(`Error executing ${planned.actionId}: ${err.message}`);
        }

        return results;
    }

    return { type: 'error', error: `Unhandled plan intent: ${plan.intent}` };
}

/**
 * Plan a natural language request and run what it produces -- the chat's entry point into the
 * Gibbs tree, so a caller names neither the planner nor the workspace.
 *
 * Returns the planner's own result, with `execution` added when a plan ran:
 *  - 'chat': a message to show. `chatSource` says whose it is -- 'planner' to show as it stands,
 *    'llm' for the caller's own conversational route to answer instead.
 *  - 'clarification': a question for the user. Pass `clarification.context` and their answer to
 *    resumeInterpretText. Destructive confirmations arrive this way, as a 'destructive_confirm'
 *    cascade; the plan stays with the planner until an answer comes back.
 *  - 'plan': a single action, already dispatched. `execution` holds the result.
 *  - 'invalid' or 'cancelled': nothing ran.
 *
 * @param {string} text - What the user asked for
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<Object>}
 */
export async function interpretText(text, { signal }={}) {
    const { textToAction } = await import('../gibbs/text-to-action');
    const result = await textToAction(text, App.Workspace, { signal });
    if (result.type !== 'plan') return result;
    return { ...result, execution: await interpretActionPlan(result.plan) };
}

/**
 * Answer an outstanding clarification and run the plan it completes, if it completes one. The
 * result shapes are interpretText's: another 'clarification' when more is needed, a dispatched
 * 'plan', or 'chat' carrying what the planner had to say instead -- a cancelled confirmation, or
 * one no longer pending.
 *
 * @param {Object} context - `clarification.context` from an earlier interpretText
 * @param {*} answer - An option's value, what the user typed, or {decision} for a confirmation
 * @returns {Promise<Object>}
 */
export async function resumeInterpretText(context, answer) {
    const { resumeFromClarification } = await import(
        '../gibbs/text-to-action/resumeFromClarification'
    );
    const { plan, needsMoreClarification, chatMessage } = resumeFromClarification(
        context, answer, App.Workspace,
    );
    if (needsMoreClarification) {
        return { type: 'clarification', clarification: needsMoreClarification };
    }
    if (plan) {
        return { type: 'plan', plan, execution: await interpretActionPlan(plan) };
    }
    return { type: 'chat', message: chatMessage || '', chatSource: 'planner' };
}
